/**
 * What all drinks share (docs/17-getraenke.md): the base fields of every drink profile, the
 * container and its size in ml, and how an article's stock in ml shows in containers and packs.
 * The core counts in ml everywhere; the app shows „4 Fl.“, „2 Kartons à 6 + 3 Fl.“.
 */
import { findAllergen } from "@household-kit/food";
import type { ProfileField } from "./profile.ts";
import type { PackUnit } from "./units.ts";
import { litres } from "./measures.ts";

/** Containers a drink comes in; empty means Flasche. Short and plural for showing stock. */
export const CONTAINERS: Readonly<Record<string, { short: string; plural: string }>> = {
  Flasche: { short: "Fl.", plural: "Fl." },
  Dose: { short: "Dose", plural: "Dosen" },
  Fass: { short: "Fass", plural: "Fässer" },
  "Bag-in-Box": { short: "Box", plural: "Boxen" },
  Karton: { short: "Karton", plural: "Kartons" },
  // Containers of the pantry (docs/18-vorratsbuch.md, Einheiten).
  Packung: { short: "Pck.", plural: "Pck." },
  Glas: { short: "Glas", plural: "Gläser" },
  Beutel: { short: "Beutel", plural: "Beutel" },
  Becher: { short: "Becher", plural: "Becher" },
  Tube: { short: "Tube", plural: "Tuben" },
  Rolle: { short: "Rolle", plural: "Rollen" },
  Netz: { short: "Netz", plural: "Netze" },
  Schale: { short: "Schale", plural: "Schalen" },
  Tafel: { short: "Tafel", plural: "Tafeln" },
  Stück: { short: "Stk.", plural: "Stk." },
  // A freezer box of something cooked at home (C8).
  Box: { short: "Box", plural: "Boxen" },
};

/** Additives a menu has to name (docs/17-getraenke.md, Pflichtangaben); to be checked before the card shows them. */
export const ADDITIVES = [
  "mit Farbstoff", "mit Konservierungsstoff", "mit Antioxidationsmittel", "mit Süßungsmittel", "mit Phosphat", "geschwefelt",
  "enthält eine Phenylalaninquelle", "koffeinhaltig", "chininhaltig", "erhöhter Koffeingehalt",
] as const;

/**
 * The 14 allergens of the food information regulation: stored by the ids of household-kit `food`
 * (gluten, eggs, sulphites …), as SpeisePlan and SpeiseKarte know them; shown by the names cards
 * and tables have always had here. Values stored before (the names) are read too, so nothing had
 * to be migrated; a change stores the ids.
 */
export const ALLERGEN_NAMES: Readonly<Record<string, string>> = {
  gluten: "Glutenhaltiges Getreide",
  crustaceans: "Krebstiere",
  eggs: "Eier",
  fish: "Fisch",
  peanuts: "Erdnüsse",
  soy: "Soja",
  milk: "Milch",
  nuts: "Schalenfrüchte",
  celery: "Sellerie",
  mustard: "Senf",
  sesame: "Sesam",
  sulphites: "Sulfite",
  lupins: "Lupinen",
  molluscs: "Weichtiere",
};

/** The names, as the form and the tables offer them. */
export const ALLERGENS = Object.values(ALLERGEN_NAMES);

/** The id of an allergen however it is written: an id, a name of any of the apps („Milch und Laktose“, „Ei“); null if none. */
export const allergenId = (v: string): string | null => findAllergen(v)?.id ?? null;

const list = (v: unknown): string[] => (Array.isArray(v) ? v : typeof v === "string" ? v.split(",") : []).filter((x): x is string => typeof x === "string").map((x) => x.trim()).filter(Boolean);

/** As stored: the ids, each once; what is no allergen stays as written. */
export const allergenIds = (v: unknown): string[] => [...new Set(list(v).map((x) => allergenId(x) ?? x))];

/** As shown: the names; what is no allergen stays as written. */
export const allergenNames = (v: unknown): string[] => [...new Set(list(v).map((x) => ALLERGEN_NAMES[allergenId(x) ?? ""] ?? x))];

/** Alcohol: a number (13,5) or an upper bound („< 0,5“, what labels of alcohol-free drinks say). */
export type Alcohol = number | { below: number };

/** Reads „13,5“, „13,5 %“, „< 0,5“, „unter 0,5“, „max. 0,5“, „0,0“; a cell formatted as percent (0.135) too. */
export function parseAlcohol(raw: unknown): { value: Alcohol } | { error: string } {
  if (typeof raw === "number") return raw >= 0 && raw <= 100 ? { value: raw > 0 && raw < 1 ? Math.round(raw * 1000) / 10 : raw } : { error: `Alkohol „${raw}“ ist keine Zahl` };
  if (raw && typeof raw === "object" && typeof (raw as { below?: unknown }).below === "number") return { value: raw as Alcohol };
  const t = String(raw ?? "").trim().toLowerCase().replace(/\s*(%|vol\.?)\s*/g, "").trim();
  const m = /^(<|≤|unter|max\.?|bis|höchstens)?\s*(\d+(?:[.,]\d+)?)$/.exec(t);
  if (!m) return { error: `Alkohol „${String(raw)}“ ist keine Zahl` };
  const n = Number(m[2]!.replace(",", "."));
  if (!(n >= 0 && n <= 100)) return { error: `Alkohol „${String(raw)}“ ist keine Zahl` };
  return { value: m[1] ? { below: n } : n };
}

/** „13,5“, „< 0,5“ (without unit). */
export const formatAlcohol = (v: unknown): string =>
  typeof v === "number" ? String(v).replace(".", ",") : v && typeof v === "object" && typeof (v as { below?: unknown }).below === "number" ? `< ${String((v as { below: number }).below).replace(".", ",")}` : "";

/** Alcohol-free: at most 0,5 % vol. (also „< 0,5“). Unknown is not alcohol-free. */
export function isAlcoholFree(v: unknown): boolean {
  if (typeof v === "number") return v <= 0.5;
  return Boolean(v && typeof v === "object" && typeof (v as { below?: unknown }).below === "number" && (v as { below: number }).below <= 0.5);
}

/** The profile of an article; without one it is a wine (everything made before F18). */
export const profileOf = (f: Record<string, unknown>) => (typeof f.profile === "string" && f.profile !== "" ? f.profile : "wein");

/**
 * The container size in ml: a number since F18; the text of before („0,75“, „1,5“) is read too, for
 * tables and data from before the migration. Null when unknown.
 */
export function sizeOf(f: Record<string, unknown>): number | null {
  const v = f.size;
  if (typeof v === "number" && v > 0) return Math.round(v);
  if (typeof v === "string") {
    const n = Number(v.replace(",", ".").replace(/\s*l$/i, ""));
    if (Number.isFinite(n) && n > 0) return Math.round(n <= 60 ? n * 1000 : n);
  }
  return null;
}

/** How a size shows next to a name when it is not the usual one: „(0,375 l)“. */
export const sizeNote = (ml: number | null, usual = 750) => (ml !== null && ml !== usual ? `(${litres(ml)})` : "");

/** The article counts in ml with a container (a drink since F18). */
export const isMl = (f: Record<string, unknown>) => f.unit === "ml" && sizeOf(f) !== null;

/**
 * The container as a unit of the article: „Fl.“ with its size as factor. A drink counts in ml (a
 * bottle when none is named); a pantry article in g, ml or Stk. with its content per container
 * („Dose“, 400 g; a pack when none is named).
 */
export function containerUnit(f: Record<string, unknown>): PackUnit | null {
  const size = sizeOf(f);
  if (size === null) return null;
  const pantry = f.profile === "lebensmittel" || f.profile === "haushalt";
  if (f.unit !== "ml" && !(pantry && (f.unit === "g" || f.unit === "Stk."))) return null;
  // Counted in pieces (eggs): the box is only what one buys (pantry.ts, COUNT_BY).
  if (pantry && f.unit === "Stk." && f.countBy === "Stück") return null;
  const named = typeof f.container === "string" && f.container in CONTAINERS ? f.container : pantry ? "Packung" : "Flasche";
  const c = CONTAINERS[named]!;
  return { name: c.short, plural: c.plural, factor: size };
}

/**
 * The units an article is counted and booked in, smallest first: the container and the packs for a
 * drink (factors in ml), else the base unit and its packs. `base` is what a rest is shown in.
 */
export function articleUnits(f: Record<string, unknown>): { base: string; units: PackUnit[] } {
  const base = typeof f.unit === "string" && f.unit !== "" ? f.unit : "Stk.";
  const packs = (Array.isArray(f.packs) ? (f.packs as PackUnit[]) : []).filter((p) => typeof p?.factor === "number" && p.factor > 0);
  const container = containerUnit(f);
  const first = container ?? { name: base, factor: 1 };
  return { base, units: [first, ...packs.filter((p) => p.factor > first.factor)].sort((a, b) => a.factor - b.factor) };
}

/**
 * A quantity in the largest fitting units: 3000 ml with a 750-ml bottle and a carton of 6 → „4 Fl.“,
 * 6000 → „1 Karton à 6 + 2 Fl.“; a rest below the smallest unit in the base unit („+ 200 ml“).
 * Negative quantities show with a minus in front of the whole expression.
 */
export function formatUnits(qty: number, units: readonly PackUnit[], base: string): string {
  const sorted = [...units].sort((a, b) => b.factor - a.factor);
  const smallest = sorted.at(-1) ?? { name: base, factor: 1 };
  if (qty === 0) return `0 ${smallest.plural ?? smallest.name}`;
  const sign = qty < 0 ? "−" : "";
  let rest = Math.abs(qty);
  const parts: string[] = [];
  for (const u of sorted) {
    const n = Math.floor(rest / u.factor);
    if (n > 0) {
      parts.push(`${n} ${n === 1 ? u.name : (u.plural ?? u.name)}`);
      rest -= n * u.factor;
    }
  }
  if (rest > 0) parts.push(`${rest} ${base}`);
  return sign + parts.join(" + ");
}

/** Containers in a quantity (bottles in 3000 ml: 4); the quantity itself for an article without container. */
export function containers(qty: number, f: Record<string, unknown>): number {
  const c = containerUnit(f);
  return c ? qty / c.factor : qty;
}

/** Labels of the base fields, per profile: „Erzeuger“ for wine, „Brauerei“ for beer. */
export interface DrinkLabels {
  producer: string;
  producerHint?: string;
  name: string;
  nameHint?: string;
  kind: string;
  kindHint?: string;
  kinds: readonly string[];
  size: string;
  sizes: readonly number[];
  containers: readonly string[];
  /** Hint of the container field; default „leer = Flasche“. */
  containerHint?: string;
  salePrice: string;
  glassHint: string;
  listNoHint: string;
  countries?: readonly string[];
}

/** Fields a profile adds in its places: after the kind (identity), with the origin, among „Mehr“. */
export interface DrinkExtension {
  identity?: readonly ProfileField[];
  origin?: readonly ProfileField[];
  /** Before Bio and Alkohol (Geschmack). */
  moreFirst?: readonly ProfileField[];
  /** After Alkohol (Verschluss, Trinkfenster). */
  more?: readonly ProfileField[];
}

/**
 * The base of every drink profile (docs/17-getraenke.md, Ein Grundgerüst), keys the same in all
 * profiles, labels from the profile. The keys `wine` (Name) and `color` (Art) stay as they were:
 * `name` is the name shown, and data, tables and cards already use them.
 */
export function drinkFields(l: DrinkLabels, ext: DrinkExtension = {}): ProfileField[] {
  return [
    { key: "producer", label: l.producer, type: "text", level: "family", required: true, filter: true, group: "main", ...(l.producerHint ? { hint: l.producerHint } : {}) },
    { key: "wine", label: l.name, type: "text", level: "family", required: true, group: "main", ...(l.nameHint ? { hint: l.nameHint } : {}) },
    { key: "color", label: l.kind, type: "choice", level: "family", required: true, filter: true, options: l.kinds, group: "main", ...(l.kindHint ? { hint: l.kindHint } : {}) },
    ...(ext.identity ?? []),
    { key: "size", label: l.size, type: "size", level: "article", required: true, sizes: l.sizes, unit: "l", group: "main", hint: "nach der ersten Buchung fest" },
    { key: "country", label: "Land", type: "text", level: "family", filter: true, ...(l.countries ? { options: l.countries } : {}), group: "origin" },
    { key: "region", label: "Region", type: "text", level: "family", filter: true, group: "origin", hint: "z. B. Toskana, Pfalz" },
    ...(ext.origin ?? []),
    { key: "listNo", label: "Kartennummer", type: "text", level: "article", group: "gastro", hint: l.listNoHint, private: { label: "Nummer", hint: "eigene Nummer, etwa der Regalplatz" } },
    { key: "salePrice", label: l.salePrice, type: "money", level: "article", group: "gastro", unit: "€" },
    { key: "cardText", label: "Kartentext", type: "longtext", level: "family", group: "gastro", hint: "Beschreibung für die Getränkekarte, zwei bis drei Sätze" },
    { key: "offCard", label: "Nicht auf der Karte", type: "yesno", level: "article", group: "gastro", hint: "trotz Preis und Bestand nicht auf die Getränkekarte" },
    { key: "byGlass", label: "Offen (Glaspreise)", type: "glass", level: "article", group: "gastro", hint: l.glassHint },
    ...(ext.moreFirst ?? []),
    { key: "organic", label: "Bio", type: "yesno", level: "family", group: "more" },
    { key: "alcohol", label: "Alkohol", type: "alcohol", level: "article", unit: "% vol.", group: "more", hint: "Zahl oder Obergrenze, z. B. „13,5“ oder „< 0,5“" },
    ...(ext.more ?? []),
    { key: "additives", label: "Zusatzstoffe", type: "multi", level: "family", options: ADDITIVES, group: "more", hint: "für die Fußnoten der Getränkekarte" },
    { key: "allergens", label: "Allergene", type: "multi", level: "family", options: ALLERGENS, group: "more" },
    // Without it a drink without allergens counts as not kept up: the filter on the net never hides it (F29).
    { key: "allergenFree", label: "Keine der 14 Allergene", type: "yesno", level: "family", group: "more", hint: "für den Allergenfilter der Karte im Netz; ohne Allergene und ohne dieses Häkchen gilt ein Getränk als „nicht angegeben“" },
    { key: "container", label: "Gebinde", type: "choice", level: "article", options: l.containers, group: "more", hint: l.containerHint ?? "leer = Flasche" },
    { key: "note", label: "Notiz", type: "text", level: "family", group: "more" },
  ];
}
